import { ChevronLeft, PencilLine, Plus, Trash2, Copy } from "lucide-react";
import _ from 'lodash';
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { Blueprint } from "@/types";
import { Input, InputNumber, Popover, Select } from "antd";

interface CreateAutomationSidebarProps {
  setActiveTab: (value: string) => void;
  blueprints: any[];
  selectedBlueprint: Blueprint | null;
  setSelectedBlueprint: (value: Blueprint) => void;
}

interface CreateAutomationHeaderProps {
  handleBack: () => void;
  navigate?: (link: string) => void;
  onCreate: () => void;
}

interface Condition {
  type: string;
  operator: '>' | '<' | '>=' | '<=';
  value: number | null;
  logical_operator?: 'AND' | 'OR';
  parameter?: string;
  digit?: number;
  max?: number;
  min?: number;
  suffix?: string;
}

interface ActionParameter {
  device_ids?: string[];
  mode?: string;
  value?: number;
}

interface Action {
  id?: string;
  name?: string;
  value?: number;
  parameters?: ActionParameter[];
}

interface State {
  id: string;
  conditions: Condition[];
  actions: Action[];
}

interface Constraint {
  id: string;
  device?: string;
  device_ids?: string[];
  attributes: {
    mode?: string;
    setpoint?: number;
  };
}

interface Parameter {
  name: string;
  display_name: string;
  unit: string;
  constraints: {
    decimal_places_allowed: number;
    max_value: number;
    min_value: number;
  };
}

interface Device {
  id: string;
  name: string;
}

interface CreateDetailProps {
  activeTab: string;
  states: State[];
  setStates: (states: State[]) => void;
  constraints: Constraint[];
  setConstraints: (constraints: Constraint[]) => void;
  availableDevices: Device[];
  parameters: Parameter[];
  selectedBlueprint: string | null;
  editMode?: boolean;
  disabled?: boolean;
}

interface CreateAutomationMainSectionProps {
  automationName: string;
  setAutomationName: (name: string) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  states: State[];
  setStates: (states: State[]) => void;
  parameters: Parameter[];
  interval: number[];
  setInterval: (interval: number[]) => void;
  selectedBlueprint: string | null;
  constraints: Constraint[];
  setConstraints: (constraints: Constraint[]) => void;
  availableDevices: Device[];
}

export const CreateDetail = ({
  activeTab, states, setStates, constraints, setConstraints, availableDevices,
  parameters, selectedBlueprint, editMode, disabled,
}: CreateDetailProps) => {
  const handleRemoveState = (stateIdx: number) => {
    const newStates = [...states];
    newStates.splice(stateIdx, 1);
    setStates(newStates);
  };

  const handleDuplicateState = (stateIdx: number) => {
    const stateToDuplicate = states[stateIdx];
    const duplicatedState = _.cloneDeep(stateToDuplicate);
    const newStates = [...states];
    newStates.splice(stateIdx + 1, 0, duplicatedState);
    setStates(newStates);
  };

  const handleAddCondition = (stateIdx: number) => {
    setStates(
      states.map((state, idx) => idx === stateIdx ? {
        ...state,
        conditions: [
          ...state.conditions,
          {
            type: '',
            operator: '>' as const,
            value: null,
            logical_operator: 'AND',
          },
        ]
      } : state)
    );
  };

  const handleRemoveCondition = (stateIdx: number, conditionIdx: number) => {
    const newStates = _.cloneDeep(states);
    newStates[stateIdx].conditions.splice(conditionIdx, 1);
    setStates(newStates);
  };

  const handleRemoveAction = (stateIdx: number, actionIdx: number) => {
    const newStates = _.cloneDeep(states);
    if (selectedBlueprint === 'Flowsave') {
      // For Flowsave, we have an array of actions
      if (Array.isArray(newStates[stateIdx].actions)) {
        newStates[stateIdx].actions.splice(actionIdx, 1);
      }
    } else {
      // For other blueprints, we have parameters array inside actions object
      if (newStates[stateIdx].actions && Array.isArray(newStates[stateIdx].actions.parameters)) {
        newStates[stateIdx].actions.parameters.splice(actionIdx, 1);
      }
    }
    setStates(newStates);
  };

  const handleAddParameter = (stateIdx: number) => {
    setStates(
      states.map((state, idx) => idx === stateIdx ? {
        ...state,
        actions: {
          ...state.actions,
          parameters: [
            ...(state.actions.parameters || []),
            {
              device_ids: [],
              mode: 'Cool',
              value: 26
            },
          ]
        }
      } : state)
    );
  };

  const handleAddState = () => {
    // Get the first state from existing states to use as a template for actions structure
    const existingState = states && states.length > 0 ? states[0] : null;

    // Initialize empty actions based on the blueprint type
    let defaultActions;
    if (selectedBlueprint === 'Flowsave') {
      // For Flowsave, we need an array of actions with two default actions
      defaultActions = [
        {
          name: 'Maintain number of AHU on Fan mode',
          value: 3
        },
        {
          name: 'Set all AHU Setpoint to',
          value: 25
        }
      ];
    } else {
      // For other blueprints, we need an object with parameters array
      defaultActions = {
        name: existingState?.actions?.name || '',
        parameters: [{
          device_ids: [],
          mode: 'Cool',
          value: 26
        }]
      };
    }

    if (!states || states.length === 0) {
      setStates([
        {
          id: `state-1`,
          conditions: [],
          actions: defaultActions,
        }]);
    }
    else setStates([
      ...states,
      {
        id: `state-${states.length + 1}`,
        conditions: [],
        actions: defaultActions,
      },
    ]);
  };

  const handleAddConstraint = () => {
    if (!constraints) {
      setConstraints([
        {
          id: `con-1`,
          device: 'AHU 01',
          attributes: {
            mode: 'cool',
            setpoint: 25,
          }
        },
      ])
    }
    else setConstraints([
      ...constraints,
      {
        id: `con-${constraints.length + 1}`,
        device: 'AHU 01',
        attributes: {
          mode: 'cool',
          setpoint: 25,
        }
      },
    ]);
  };

  const handleRemoveConstraint = (index: number) => {
    const newConstraints = [...constraints];
    newConstraints.splice(index, 1);
    setConstraints(newConstraints);
  };

  return (
    <>
      {activeTab === 'states' && (
        <div className="flex flex-col gap-3 pb-14">
          {
            selectedBlueprint === 'Flowsave' &&
            <div className="flex justify-between items-center">
              <p className="paragraph-14 font-bold text-dark-blue">Flowsave path</p>
              {
                !editMode &&
                <p className="paragraph-12 text-dark-grey">Automation logic will run starting from State 1 until the condition is TRUE</p>
              }
            </div>
          }
          {states?.map((state, stateIdx) => (
            <div
              key={stateIdx}
              className="p-2.5 bg-gradient-to-tr from-white/20 to-white/20 rounded-xl border border-light-grey"
            >
              <div className="flex justify-between items-center mb-1.5">
                <h3 className="heading-7 text-dark-blue">
                  State {stateIdx + 1}
                </h3>
                {
                  !disabled && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-4.5 w-4.5 p-1 bg-bg rounded hover:bg-primary-light"
                        onClick={() => handleDuplicateState(stateIdx)}
                        title="Duplicate state"
                      >
                        <Copy className="h-3.5 w-3.5 text-primary" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-4.5 w-4.5 p-1 bg-bg rounded hover:bg-red-50"
                        onClick={() => handleRemoveState(stateIdx)}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-danger" />
                      </Button>
                    </div>
                  )
                }
              </div>

              <div className="flex gap-2">
                {/* Conditions */}
                <div className="flex-1 p-2 bg-white rounded-lg border border-light-grey">
                  <div className="paragraph-10 text-dark-grey mb-1">Conditions</div>
                  <div className="space-y-1">
                    {
                      state?.conditions?.map((condition, idx) => (
                        <div key={idx} className="flex items-center gap-1.5">
                          {
                            idx > 0 && <Select
                              value={condition.logical_operator}
                              disabled={disabled}
                              onChange={(e) => {
                                const newStates = _.cloneDeep(states);
                                newStates[stateIdx].conditions[idx].logical_operator = e;
                                setStates(newStates);
                              }}
                              options={[{
                                value: 'AND', label: 'AND',
                              }, {
                                value: 'OR', label: 'OR',
                              }]}
                              className="w-[60px] paragraph-10"
                            />
                          }
                          <Select
                            value={condition.parameter}
                            disabled={disabled}
                            onChange={(e, option) => {
                              const newStates = _.cloneDeep(states);
                              newStates[stateIdx].conditions[idx].parameter = e;
                              newStates[stateIdx].conditions[idx].digit = option?.digit;
                              newStates[stateIdx].conditions[idx].max = option?.max;
                              newStates[stateIdx].conditions[idx].min = option?.min;
                              newStates[stateIdx].conditions[idx].suffix = option?.suffix;
                              newStates[stateIdx].conditions[idx].operator = '>';
                              newStates[stateIdx].conditions[idx].value = null;
                              setStates(newStates);
                            }}
                            options={parameters.map(parameter => ({
                              value: parameter.name, label: parameter.display_name,
                              digit: parameter.constraints.decimal_places_allowed,
                              max: parameter.constraints.max_value,
                              min: parameter.constraints.min_value,
                              suffix: parameter.unit,
                            }))}
                            className='grow' />
                          <Select
                            disabled={disabled}
                            value={condition.operator}
                            onChange={(e) => {
                              const newStates = _.cloneDeep(states);
                              newStates[stateIdx].conditions[idx].operator = e;
                              setStates(newStates);
                            }}
                            options={[{
                              value: '>', label: '>',
                            }, {
                              value: '<', label: '<',
                            }, {
                              value: '>=', label: '≥',
                            }, {
                              value: '<=', label: '≤',
                            }]}
                            className="w-[60px] paragraph-10"
                          />
                          <div className="flex items-center gap-1">
                            <InputNumber
                              disabled={disabled}
                              value={condition.value}
                              onChange={(value) => {
                                const newStates = _.cloneDeep(states);
                                newStates[stateIdx].conditions[idx].value = value;
                                setStates(newStates);
                              }}
                              min={condition.min}
                              max={condition.max}
                              className="w-[90px]"
                              style={{ fontSize: '12px' }}
                              controls={false}
                            />
                            {condition.suffix && (
                              <span className="paragraph-10 text-dark-grey min-w-fit">{condition.suffix}</span>
                            )}
                          </div>
                          {!disabled && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4.5 w-4.5 p-1 rounded hover:bg-red-50 ml-1"
                              onClick={() => handleRemoveCondition(stateIdx, idx)}
                            >
                              <Trash2 className="h-3.5 w-3.5 text-danger" />
                            </Button>
                          )}
                        </div>
                      ))
                    }
                    {/* Condition rows */}
                  </div>
                  {
                    !disabled &&
                    <button
                      onClick={() => handleAddCondition(stateIdx)}
                      className="mt-1.5 flex items-center gap-0.5 text-[10px] text-primary">
                      <Plus className="h-4 w-4" />
                      <p className="paragraph-10 text-primary">Add</p>
                    </button>
                  }
                </div>

                {/* Actions */}
                <div className="flex-1 p-2 bg-white rounded-lg border border-light-grey h-min">
                  <div className="paragraph-10 text-dark-grey mb-1.5">Actions</div>
                  {
                    selectedBlueprint === 'Flowsave' ?
                      <>
                        {Array.isArray(state.actions) && state.actions.map((action, idx) => (
                          <div key={idx} className="flex w-full gap-1.5 mb-1.5">
                            <Select disabled value={action.name || 'Maintain number of AHU on Fan mode'} className='grow' />
                            <Input
                              type="number"
                              suffix="°C"
                              onChange={(e) => {
                                const newStates = _.cloneDeep(states);
                                newStates[stateIdx].actions[idx].value = Number(e.target.value);
                                setStates(newStates);
                              }}
                              disabled={disabled}
                              value={action.value}
                              min={0}
                              max={100}
                              className="w-[90px] paragraph-10 h-8 px-3 rounded-md border border-light-grey focus:border-primary focus:outline-none"
                            />
                            {!disabled && selectedBlueprint !== 'Flowsave' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4.5 w-4.5 p-1 rounded hover:bg-red-50 ml-1"
                                onClick={() => handleRemoveAction(stateIdx, idx)}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-danger" />
                              </Button>
                            )}
                          </div>
                        ))}

                      </> :
                      <>
                        <div className="flex w-full gap-1.5 mb-1.5">
                          <Select disabled value={state.actions?.name} className='grow' />
                        </div>
                        {
                          state?.actions?.parameters?.map((parameter, idx) => (
                            <div key={idx} className="flex w-full gap-1.5 mb-1.5">
                              <Select options={[
                                {
                                  label: 'All',
                                  value: 'all',
                                },
                                ...availableDevices?.map(device => ({
                                  label: device.name,
                                  value: device.id,
                                }))
                              ]}
                                value={parameter.device_ids} className='grow'
                                onChange={(e) => {
                                  const newStates = _.cloneDeep(states);
                                  // Check if 'all' is selected
                                  if (Array.isArray(e) && e.includes('all')) {
                                    // If 'all' is selected, select all device IDs
                                    newStates[stateIdx].actions.parameters[idx].device_ids = availableDevices?.map(device => device.id) || [];
                                  } else {
                                    newStates[stateIdx].actions.parameters[idx].device_ids = e;
                                  }
                                  setStates(newStates);
                                }}
                                disabled={disabled}
                                mode="multiple"
                                placeholder="Please Select"
                                maxTagCount="responsive"
                              />
                              <Select
                                value={parameter.mode} className='w-[90px]'
                                options={[{
                                  value: 'Cool', label: 'Cool',
                                }, {
                                  value: 'Fan', label: 'Fan',
                                }, {
                                  value: 'Dry', label: 'Dry',
                                }]}
                                disabled={disabled}
                                onChange={(e) => {
                                  const newStates = _.cloneDeep(states);
                                  newStates[stateIdx].actions.parameters[idx].mode = e;
                                  setStates(newStates);
                                }}
                              />
                              <Input
                                type="number"
                                suffix="°C"
                                disabled={parameter.mode !== 'Cool' || disabled}
                                onChange={(e) => {
                                  const newStates = _.cloneDeep(states);
                                  newStates[stateIdx].actions.parameters[idx].value = Number(e.target.value);
                                  setStates(newStates);
                                }}
                                value={parameter?.value}
                                min={0}
                                max={100}
                                className="w-[90px] paragraph-10 h-8 px-3 rounded-md border border-light-grey focus:border-primary focus:outline-none"
                              />
                              {!disabled && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-4.5 w-4.5 p-1 rounded hover:bg-red-50 ml-1"
                                  onClick={() => handleRemoveAction(stateIdx, idx)}
                                >
                                  <Trash2 className="h-3.5 w-3.5 text-danger" />
                                </Button>
                              )}
                            </div>
                          ))
                        }
                        {
                          !disabled &&
                          <button
                            onClick={() => handleAddParameter(stateIdx)}
                            className="mt-1.5 flex items-center gap-0.5 text-[10px] text-primary">
                            <Plus className="h-4 w-4" />
                            <p className="paragraph-10 text-primary">Add</p>
                          </button>
                        }
                      </>
                  }
                </div>
              </div>
            </div>
          ))}
          {
            !disabled &&
            <Button
              variant="outline"
              size="md"
              className="w-min text-primary border-primary bg-white"
              onClick={handleAddState}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              <p className="button-m">Add State</p>
            </Button>
          }
        </div>
      )}
      {activeTab === 'constraints' && (
        <div className="space-y-4 pb-14">
          <div className="flex justify-between items-center">
            <p className="paragraph-14 font-bold text-dark-blue">Device Constraints</p>
            {
              !disabled &&
              <p className="paragraph-12 text-dark-grey">Conditions settings will not be violated</p>
            }
          </div>

          <div className="space-y-4">
            {constraints?.map((constraint, index) => (
              <div
                key={constraint.id}
                className="p-2.5 bg-gradient-to-tr from-white/20 to-white/20 rounded-xl border border-light-grey"
              >
                <div className="flex justify-between items-center mb-1.5">
                  <h3 className="heading-7 text-dark-blue">
                    Constraint {index + 1}
                  </h3>
                  {
                    !disabled &&
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4.5 w-4.5 p-1 bg-bg rounded hover:bg-red-50"
                      onClick={() => handleRemoveConstraint(index)}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-danger" />
                    </Button>
                  }
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 h-min p-2 bg-white rounded-lg border border-light-grey">
                    <div className="paragraph-10 text-dark-grey mb-1">Devices</div>
                    <div className="space-y-1 w-full">
                      <Select options={[
                        {
                          label: 'All',
                          value: 'all',
                        },
                        ...availableDevices?.map(device => ({
                          label: device.name,
                          value: device.id,
                        }))
                      ]} className='w-full'
                        value={constraint.device_ids}
                        disabled={disabled}
                        onChange={(e) => {
                          const newConstraints = _.cloneDeep(constraints);
                          // Check if 'all' is selected
                          if (Array.isArray(e) && e.includes('all')) {
                            // If 'all' is selected, select all device IDs
                            newConstraints[index].device_ids = availableDevices?.map(device => device.id) || [];
                          } else {
                            newConstraints[index].device_ids = e;
                          }
                          setConstraints(newConstraints);
                        }}
                        mode="multiple"
                        placeholder="Please Select"
                        maxTagCount="responsive" />
                    </div>
                  </div>

                  <div className="flex-1 p-2 bg-white rounded-lg border border-light-grey">
                    <div className="paragraph-10 text-dark-grey mb-1">Attribute</div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Select value='Mode' disabled className='w-1/2' />
                        <Select value={constraint?.attributes?.mode} className='w-1/2'
                          options={[{
                            value: 'cool', label: 'Cool',
                          }, {
                            value: 'fan', label: 'Fan',
                          }, {
                            value: 'dry', label: 'Dry',
                          }]}
                          disabled={disabled}
                          onChange={(e) => {
                            const newConstraints = _.cloneDeep(constraints);
                            newConstraints[index].attributes.mode = e;
                            setConstraints(newConstraints);
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Select value='Set Point' disabled className='w-1/2' />
                        <Input
                          type="number"
                          suffix="°C"
                          value={constraint?.attributes?.setpoint}
                          disabled={disabled}
                          onChange={(e) => {
                            const newConstraints = _.cloneDeep(constraints);
                            newConstraints[index].attributes.setpoint = Number(e.target.value);
                            setConstraints(newConstraints);
                          }}
                          className="w-1/2 paragraph-10 h-8 px-3 rounded-[10px] border border-light-grey focus:border-primary focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {
            !disabled &&
            <Button
              variant="outline"
              className="text-primary border-primary"
              onClick={handleAddConstraint}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Constraint
            </Button>
          }
        </div>
      )}
    </>
  )
}

export const CreateAutomationMainSection = ({
  automationName, setAutomationName, activeTab, setActiveTab, states, setStates, parameters,
  interval, setInterval, selectedBlueprint, constraints, setConstraints, availableDevices,
}: CreateAutomationMainSectionProps) => {
  return (
    <div className="flex-1 p-6 overflow-y-auto">
      {/* Automation Configuration */}
      <div className="space-y-6 mx-auto">
        {/* Name and Interval */}
        <div className="flex justify-between">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <p className="paragraph-14 font-semibold text-dark-blue">
                Automation Name
              </p>
              <input
                type="text"
                value={automationName}
                onChange={(e) => setAutomationName(e.target.value)}
                placeholder="Automation name"
                className="w-[330px] paragraph-10 max-w-md h-9 px-3 rounded-md border border-light-grey focus:border-primary focus:outline-none"
              />
            </div>
            {
              selectedBlueprint === 'Flowsave' && (
                <div className="flex items-center gap-3">
                  <p className="paragraph-14 font-semibold text-dark-blue">
                    Set Interval
                  </p>
                  <Input
                    type="number"
                    suffix="minutes"
                    value={interval[0]}
                    onChange={(e) => setInterval([Number(e.target.value), 0])}
                    className="w-32 paragraph-10 h-9 px-3 rounded-md border border-light-grey focus:border-primary focus:outline-none"
                  />
                </div>
              )
            }
          </div>
          {
            selectedBlueprint === 'Flowsave' &&
            <div>
              <div className="flex gap-3">
                <button
                  onClick={() => setActiveTab('states')}
                  className={cn(
                    "px-[1px] py-1 paragraph-12 border-b-2 transition-colors",
                    activeTab === 'states'
                      ? "border-primary text-primary"
                      : "border-white-01 text-dark-grey"
                  )}
                >
                  States Setting
                </button>
                <button
                  onClick={() => setActiveTab('constraints')}
                  className={cn(
                    "px-[1px] py-1 paragraph-12 border-b-2 transition-colors",
                    activeTab === 'constraints'
                      ? "border-primary text-primary"
                      : "border-white-01 text-dark-grey"
                  )}
                >
                  Constraints Setting
                </button>
              </div>
            </div>
          }
        </div>
        <CreateDetail
          activeTab={activeTab}
          states={states}
          setStates={setStates}
          constraints={constraints}
          setConstraints={setConstraints}
          parameters={parameters}
          selectedBlueprint={selectedBlueprint}
          availableDevices={availableDevices} />

      </div>
    </div>
  )
}

export const CreateAutomationSidebar = ({ blueprints, selectedBlueprint, setSelectedBlueprint, setActiveTab }: CreateAutomationSidebarProps) => (
  <div className="flex flex-col w-[340px] p-6 overflow-y-auto gap-2.5 bg-light-grey border-r border-white-blue">
    <p className="text-dark-grey heading-7">Select Blueprint</p>
    <div className="space-y-2">
      {blueprints.map((blueprint) => (
        <button
          key={blueprint.name}
          onClick={() => {
            setSelectedBlueprint(blueprint.name)
            if (blueprint.name !== 'Flowsave') {
              setActiveTab('states')
            }
          }}
          className={cn(
            "w-full p-3 rounded-lg border text-left transition-colors",
            selectedBlueprint === blueprint.name
              ? "bg-white border-white-blue shadow-widget"
              : "border-bg hover:border-white-blue hover:bg-bg"
          )}
        >
          <div className="flex items-center gap-2">
            <div className={cn("w-4 h-4 rounded flex items-center justify-center", blueprint.color)}>
              {blueprint.icon}
            </div>
            <span className="text-black paragraph-12">{blueprint.name}</span>
          </div>
        </button>
      ))}
    </div>
  </div>
)

export const CreateAutomationHeader = ({ handleBack, onCreate }: CreateAutomationHeaderProps) => (
  <div className="flex justify-between items-center pb-3 border-b border-white-blue px-4 mx-[-16px]">
    <Button
      variant="ghost"
      size="sm"
      className="text-dark-grey"
      onClick={handleBack}
    >
      <ChevronLeft className="h-4 w-4 mr-1" />
      <h2 className="heading-6 text-dark-blue">Create Automation</h2>
    </Button>
    <div className="flex gap-2">
      <Button
        variant="default"
        size="md"
        className="bg-primary text-white"
        onClick={onCreate}
      >
        <Plus className="h-4 w-4 mr-1.5" />
        <p className="button-s">Create</p>
      </Button>
    </div>
  </div>
)